import React from "react";
import "./SourceIcon.scss";

export interface SourceIconProps {
  /** The artwork, as the view models build it: `{iconBase}/{sourceTypeId}.png`. */
  src: string;
  /**
   * The connection's colour. The ring carries it where the board set one, so a
   * Jira item reads as Jira on the card and in the task form alike; without
   * one the ring is the neutral stroke.
   */
  colour?: string;
  /** Size of the artwork in pixels. The plate and the ring sit outside it. */
  size?: number;
  /** Decorative by default. Set it where the icon is the only label. */
  alt?: string;
  className?: string;
}

/**
 * A source's logo, wherever one is shown: the board card's links, the task
 * form's toolbar and its Connections card.
 *
 * It is one component so the three parts of the cue cannot drift apart — the
 * plate the logo sits on (style.scss, since it also has to reach the icons
 * that are not this component), the padding that keeps the artwork off the
 * ring, and the ring itself in the connection's colour.
 */
const SourceIcon: React.FC<SourceIconProps> = ({
  src,
  colour,
  size = 16,
  alt = "",
  className,
}) => (
  <img
    src={src}
    alt={alt}
    aria-hidden={alt ? undefined : true}
    width={size}
    height={size}
    loading="lazy"
    draggable={false}
    className={`source-icon${colour ? " source-icon--accented" : ""}${
      className ? ` ${className}` : ""
    }`}
    // Per element rather than through a class: this renders once per source
    // task, so a shared class name would have every item on the page
    // redefining the same rule and the last one would win.
    style={colour ? { borderColor: colour } : undefined}
  />
);

export default SourceIcon;
